import React, { useRef, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useParams } from "react-router-dom";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import GOQiiButton from "../Components/GoqiiButton";
import { navigateWithFai } from "../utils/Utils";
import { useTranslation } from "react-i18next";

// Shimmer helpers (shared across lifestyle cards)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const NutritionSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        position: "relative",
      }}
    >
      {/* SectionHeader skeleton */}
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />

          {/* Title + subtitle */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                width: "60%",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                width: "40%",
                borderRadius: 8,
              }}
            />
          </div>

          {/* Info icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 20,
              height: 20,
              borderRadius: 999,
            }}
          />
        </div>
      </div>

      {/* Chart skeleton */}
      <div
        style={{
          marginTop: 12,
          padding: "0 16px 80px 16px",
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 220,
            borderRadius: 16,
          }}
        />
      </div>

      {/* Bottom button skeleton */}
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 40,
            width: 220,
            borderRadius: 999,
          }}
        />
      </div>
    </div>
  );
};

const Nutrition = () => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const [nutritionData, setNutritionData] = useState(null);
  useEffect(() => {
    const { encryptedId } = getUrlParams();

    if (!encryptedId) return;

    lifestyleApi
      .foodData(encryptedId)
      .then((response) => {
        if (response?.data?.code === 200) {
          setNutritionData(response.data.data);
        } else {
          console.error("Invalid response:", response);
        }
      })
      .catch((err) => console.error("Error loading nutrition data:", err));
  }, []);


  if (!nutritionData) return <NutritionSkeleton />;


  const options = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      spacingTop: 10,
      spacingBottom: 100,
      spacingLeft: 20,
      spacingRight: 20,
      height: 200,
      style: { fontFamily: "'Open Sans', sans-serif" },
      events: {
        render() {
          const chart = this;
          const isSmallMobile = chart.chartWidth < 480;

          if (chart.customElements)
            chart.customElements.forEach((el) => el.destroy());
          chart.customElements = [];


          const pieCenterY = chart.plotTop + chart.plotHeight * 0.38;
          const healthMeterX = chart.plotLeft + chart.plotWidth * 0.19;
          const portionSizeX = chart.plotLeft + chart.plotWidth * 0.8

          const labelOffsetY = -50;
          const valueOffsetY = 55;

          chart.customElements.push(
            chart.renderer
              .text(t("nutrition.healthMeter"), healthMeterX, pieCenterY + labelOffsetY)
              .css({ fontSize: isSmallMobile ? "11px" : "11px", fontWeight: "bold", color: "var(--text-secondary)", fontFamily: "Open Sans, sans-serif" })
              .attr({ align: "center" })
              .add()

          );

          chart.customElements.push(
            chart.renderer
              .text("" + nutritionData.hmDataPoint, healthMeterX, pieCenterY + valueOffsetY - 10)
              // .css({ fontSize: isSmallMobile ? "9px" : "9px", fontWeight: "bold", color: "#000", fontFamily: "Open Sans, sans-serif" })
              .css({ color: "var(--text-primary)" })
              .attr({ align: "center" })
              .add()
          );

          chart.customElements.push(
            chart.renderer
              .text(t("nutrition.portionSize"), portionSizeX, pieCenterY + labelOffsetY)
              .css({ fontSize: isSmallMobile ? "11px" : "11px", fontWeight: "bold", color: "var(--text-secondary)", fontFamily: "Open Sans, sans-serif" })
              .attr({ align: "center" })
              .add()
          );

          chart.customElements.push(
            chart.renderer
              .text(nutritionData.psDataPoint, portionSizeX, pieCenterY + valueOffsetY - 10)
              // .css({ fontSize: isSmallMobile ? "9px" : "9px", fontWeight: "bold", color: "#000", fontFamily: "Open Sans, sans-serif" })
              .css({ color: "var(--text-primary)" })
              .attr({ align: "center" })
              .add()
          );

          const foodLogsY = pieCenterY + valueOffsetY + 20;
          const centerX = chart.chartWidth / 2;

          const tempFoodLogs = chart.renderer
            .text(`${t("nutrition.foodLogs")} ${nutritionData.count}`, 0, 0)
            .css({ fontSize: isSmallMobile ? "12px" : "17px", fontWeight: "bold", fontFamily: "Open Sans, sans-serif" })
            .add();
          const foodLogsWidth = tempFoodLogs.getBBox().width;
          tempFoodLogs.destroy();

          const tempHomeOutside = chart.renderer
            .text(nutritionData.homeOutside, 0, 0)
            .css({ fontSize: isSmallMobile ? "12px" : "17px", fontWeight: "bold", fontFamily: "Open Sans, sans-serif" })
            .add();
          const homeOutsideWidth = tempHomeOutside.getBBox().width;
          tempHomeOutside.destroy();

          const totalWidth = foodLogsWidth + homeOutsideWidth + 5;

          chart.customElements.push(
            chart.renderer
              .text(`${t("nutrition.foodLogs")} ${nutritionData.count}`, centerX - totalWidth / 2, foodLogsY)
              .css({ fontSize: isSmallMobile ? "12px" : "17px", fontWeight: "bold", color: "var(--text-primary)", fontFamily: "Open Sans, sans-serif" })
              .attr({ align: "left" })
              .add()
          );

          chart.customElements.push(
            chart.renderer
              .text(nutritionData.homeOutside, centerX - totalWidth / 2 + foodLogsWidth + 5, foodLogsY)
              .css({ fontSize: isSmallMobile ? "12px" : "17px", fontWeight: "bold", color: nutritionData.homeOutsideColor, fontFamily: "Open Sans, sans-serif" })
              .attr({ align: "left" })
              .add()
          );


          const totalHeight = foodLogsY + 60;
          if (chart.container && chart.container.style) {
            chart.container.style.height = totalHeight + "18px";
            // Final fix: stable height for chart section
            chart.setSize(null, 245, false);

          }
        },
      },
    },
    title: { text: null },
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 90,
        innerSize: "70%",
        size: "120%",
        dataLabels: { enabled: false },
        showInLegend: false,
        borderWidth: 1,
        borderColor: "var(--border-subtle)",
        states: { hover: { enabled: false }, inactive: { opacity: 1 } },
      },
    },
    series: [
      {
        name: "Health Meter",
        data: [
          { y: (nutritionData.hmData?.[0] / 10) * 100, color: nutritionData.stopsHM?.[0]?.[1] },
          { y: 100 - (nutritionData.hmData?.[0] / 10) * 100, color: "var(--bg-surface)" },
        ],
        center: ["15%", "60%"],
        innerSize: "55%",
      },
      {
        name: "Portion Size",
        data: [
          { y: (nutritionData.psData?.[0] / 10) * 100, color: nutritionData.stopsPS?.[0]?.[1] },
          { y: 100 - (nutritionData.psData?.[0] / 10) * 100, color: "var(--bg-surface)" },
        ],
        center: ["85%", "60%"],
        innerSize: "55%",
      },
    ],
    tooltip: { enabled: false },
    credits: { enabled: false },
  };

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        position: "relative",
      }}
    >
      <SectionHeader
        icon="https://appcdn.goqii.com/storeimg/80172_1764052300.png"
        title={nutritionData.title}
        subtitle={nutritionData.heading}
        showInfo
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        onInfoClick={() => {
          navigateWithFai("My Nutrition Awarenesss", nutritionData.infoUrl);
        }}
      />

      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />


      <div style={{
        position: "absolute",
        bottom: "10px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "100%",
        display: "flex",
        justifyContent: "center"
      }}>
        <GOQiiButton
          text={t("nutrition.viewInsights")}
          color="green"
          solid={true}
          shape="rounded_edges"
          font="bold"
          onClick={() => window.open(nutritionData.url, "_blank")}
        />
      </div>
    </div>
  );
};

export default Nutrition;